import { Typography } from '@mui/material'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { Children, HTMLAttributes } from 'react'
import { RxSlash } from 'react-icons/rx'
import { tss } from 'tss-react/mui'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'
import { OrderedOption } from './SearchInputModal'

interface SearchResultsOptionProps {
  option: OrderedOption
  searchQuery: string
  usingLiveSearch: boolean
  iconComponent: JSX.Element
  hasCellTextMatch: boolean
  cellParts: string[]
  labelParts: string[]
  props: HTMLAttributes<HTMLLIElement>
}

/**
 * Function to render a search results option with highlighting, timestamps, and icons, which
 * distinguish between path and cell text matches
 */
export const SearchResultsOption = ({
  option,
  searchQuery,
  usingLiveSearch,
  iconComponent,
  hasCellTextMatch,
  cellParts,
  labelParts,
  props,
}: SearchResultsOptionProps) => {
  const { classes } = useStyles()
  const { t } = useTypedTranslation()
  // Format the timestamp for the search history if it is not 0 (not a valid timestamp)
  const timestamp = option.timestamp
  const hasTimestamp = timestamp > 0
  const formattedTimestamp = hasTimestamp ? dayjs(timestamp).fromNow() : null

  return (
    <li
      {...props}
      key={option.order}
      className={clsx(props.className, classes.option)}
      tabIndex={0}
    >
      {hasCellTextMatch && usingLiveSearch && (
        <div className={classes.lineContainer}>
          <Typography className={classes.directTextMatchHeader} variant="body1">
            {t('search.text_match_found_in')}
          </Typography>
        </div>
      )}
      <div className={classes.lineContainer}>
        <div className={classes.optionIconContainer}>{iconComponent}</div>
        <div className={classes.optionTextContainer}>
          {option.__typename === 'TableRow' && (
            <>
              <Typography className={classes.tablePath}>{option.table.name}</Typography>
              <div className={classes.tablePathSeparator}>
                <RxSlash />
              </div>
            </>
          )}
          {Children.toArray(
            labelParts
              .filter((part): part is string => typeof part === 'string' && part.length > 0)
              .map((part, index) => {
                return (
                  <Typography
                    key={index}
                    className={
                      part.toLowerCase().trim() === searchQuery.toLowerCase().trim()
                        ? classes.highlight
                        : classes.path
                    }
                  >
                    {part}
                  </Typography>
                )
              }),
          )}
          {hasCellTextMatch && (
            <>
              <div className={classes.tablePathSeparator}>
                <RxSlash />
              </div>
              <Typography className={classes.path}>{option.matchedColumnName}</Typography>
            </>
          )}
        </div>
        <div className={clsx('searchEnter', classes.searchEnter)}>
          <Typography variant="body1">{t('search.open')} &#11152;</Typography>
        </div>
        {formattedTimestamp && (
          <Tooltip arrow title={dayjs(option.timestamp).format('ddd, MM/DD/YY [at] hh:mma')}>
            <div className={classes.historyTimestamp}>
              <Typography variant="body1">{formattedTimestamp}</Typography>
            </div>
          </Tooltip>
        )}
      </div>
      {hasCellTextMatch && usingLiveSearch && (
        <div className={classes.lineContainer}>
          <div className={classes.optionSubTextContainer}>
            <>
              {Children.toArray(
                cellParts
                  .filter((part): part is string => typeof part === 'string' && part.length > 0)
                  .map((part, index) => {
                    return (
                      <Typography
                        key={index}
                        className={
                          part.toLowerCase().trim() === searchQuery.toLowerCase().trim()
                            ? classes.directTextMatchHighlight
                            : classes.matchedCellData
                        }
                      >
                        {part}
                      </Typography>
                    )
                  }),
              )}
            </>
          </div>
        </div>
      )}
    </li>
  )
}

const useStyles = tss.withName('SearchResultsOption').create(({ theme }) => ({
  searchEnter: {
    fontSize: theme.typography.body2.fontSize,
    display: 'none',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBlock: theme.spacing(0),
    paddingInline: theme.spacing(0.75),
    color: theme.palette.common.doss.gray[8],
  },
  historyTimestamp: {
    fontSize: theme.typography.body2.fontSize,
    display: 'flex',
    minWidth: '60px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBlock: theme.spacing(0),
    paddingInline: theme.spacing(0.75),
    color: theme.palette.common.doss.gray[8],
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    width: '100%',
    padding: theme.spacing(1),
    borderRadius: '1px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  lineContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
  },
  optionTextContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    flexGrow: 1,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: '85%',
  },
  optionSubTextContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(0.75),
    paddingInlineEnd: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    paddingInlineStart: theme.spacing(0.5),
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    width: '100%',
  },
  optionIconContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    color: theme.palette.common.doss.gray[10],
    fontSize: '16px',
    paddingInlineEnd: theme.spacing(1),
  },
  tablePath: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    '& p': {
      display: 'flex',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  tablePathSeparator: {
    color: theme.palette.text.disabled,
    marginInlineStart: '1px',
    marginInlineEnd: '1px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
  },
  path: {
    display: 'inline-flex',
    whiteSpace: 'pre',
    fontSize: '14px',
  },
  matchedCellData: {
    display: 'inline-flex',
    whiteSpace: 'pre',
    fontStyle: 'italic',
    overflow: 'hidden',
    fontSize: '14px',
  },
  highlight: {
    display: 'flex',
    whiteSpace: 'pre',
    justifyContent: 'center',
    width: 'min-content',
    flexWrap: 'nowrap',
    flexShrink: 1,
    fontSize: '14px',
    background: theme.palette.common.doss.amber[5],
    color: theme.palette.common.doss.amber[12],
    fontWeight: theme.typography.fontWeightBold || 'normal',
  },
  directTextMatchHeader: {
    display: 'inline-flex',
    paddingBottom: theme.spacing(0.5),
    whiteSpace: 'pre',
    color: theme.palette.common.doss.gray[8],
  },
  directTextMatchHighlight: {
    display: 'flex',
    whiteSpace: 'pre',
    justifyContent: 'center',
    width: 'min-content',
    flexWrap: 'nowrap',
    flexShrink: 1,
    fontStyle: 'italic',
    fontSize: '14px',
    color: theme.palette.common.doss.amber[12],
    background: theme.palette.common.doss.amber[3],
  },
}))
